import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import defaultBarIndoor from "../../../../assets/images/default-bar-indoor.png";
import defaultBarOutdoor from "../../../../assets/images/default-bar-outdoor.png";
import BarContactsDropdown from "../../../../components/messaging/BarContactsDropdown";
import ConfirmModal from "../../../../components/common/ConfirmModal";
import { useArtist } from "../../../../context/ArtistContext";
import { get, del } from "../../../../services/api";
import { getWeeklyHoursDisplayLines } from "../../../../utils/barWeeklyHours";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px",
  marginTop: "10px",
};

const defaultCenter = {
  lat: 24.5551, // Key West, FL
  lng: -81.78,
};

const BarModal = ({ isOpen, onClose, bar, isMapsLoaded = false }) => {
  const { refreshGroupsBars, refreshShows } = useArtist();
  const [mapExpanded, setMapExpanded] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapZoom, setMapZoom] = useState(13);
  const [showMessageDropdown, setShowMessageDropdown] = useState(false);
  const messageButtonRef = useRef(null);
  const [myArtists, setMyArtists] = useState([]);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [selectedArtistId, setSelectedArtistId] = useState(null);
  const [showArtistSelector, setShowArtistSelector] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [futureShows, setFutureShows] = useState([]);
  const [loadingFutureShows, setLoadingFutureShows] = useState(false);
  const [showMemberBlockedModal, setShowMemberBlockedModal] = useState(false);
  const [memberBlockedOwnerName, setMemberBlockedOwnerName] = useState("");
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  if (!isOpen || !bar) {
    return null;
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getIndoorPhotos = () => {
    if (
      bar.indoor_photos &&
      bar.indoor_photos.photos &&
      bar.indoor_photos.photos.length > 0
    ) {
      return bar.indoor_photos.photos;
    }
    return [defaultBarIndoor];
  };

  const getOutdoorPhotos = () => {
    if (
      bar.outdoor_photos &&
      bar.outdoor_photos.photos &&
      bar.outdoor_photos.photos.length > 0
    ) {
      return bar.outdoor_photos.photos;
    }
    return [defaultBarOutdoor];
  };

  const formatTime = (time) => {
    if (!time) return "N/A";
    try {
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch (e) {
      return time;
    }
  };

  const getSelectedArtist = () => {
    if (!selectedArtistId) return null;
    return myArtists.find((a) => a.artist_id === selectedArtistId) || null;
  };

  const getSelectedArtistName = () => {
    const a = getSelectedArtist();
    return a ? a.stage_name : "";
  };

  const fetchFutureShowsForLeave = async (artistId) => {
    const data = await get(
      `/artists/bar/${bar.bar_id}/future-shows?artist_id=${artistId}`
    );
    if (!data.success)
      throw new Error(data.error || "Failed to load future shows");
    return data.shows || [];
  };

  const formatShowDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "short",
    });
  };

  const formatShowTime = (time) => {
    if (!time) return "";
    const parts = (time || "").split(":");
    const hour = parseInt(parts[0], 10);
    const minutes = parts[1] || "00";
    const ampm = hour >= 12 ? "pm" : "am";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes}${ampm}`;
  };

  const proceedAfterArtistChosen = async (artist) => {
    if (!artist) return;
    const isOwner = artist.role === "owner";
    const ownerName = artist.owner_name || "the owner";

    if (!isOwner) {
      setMemberBlockedOwnerName(ownerName);
      setShowMemberBlockedModal(true);
      setShowArtistSelector(false);
      setSelectedArtistId(null);
      return;
    }

    setSelectedArtistId(artist.artist_id);
    setShowArtistSelector(false);
    setLoadingFutureShows(true);
    setFutureShows([]);
    try {
      const shows = await fetchFutureShowsForLeave(artist.artist_id);
      setFutureShows(shows);
      setShowLeaveConfirm(true);
    } catch (e) {
      console.error("Error fetching future shows:", e);
      setAlertModal({
        open: true,
        title: "Error",
        message: e.message || "Failed to load future shows. Please try again.",
      });
      setSelectedArtistId(null);
    } finally {
      setLoadingFutureShows(false);
    }
  };

  const handleLeaveBarClick = () => {
    if (myArtists.length === 0) return;
    if (myArtists.length === 1) {
      proceedAfterArtistChosen(myArtists[0]);
    } else {
      setShowArtistSelector(true);
    }
  };

  const handleArtistSelect = (artistId) => {
    const artist = myArtists.find((a) => a.artist_id === artistId);
    proceedAfterArtistChosen(artist);
  };

  const handleLeaveConfirm = async () => {
    if (!selectedArtistId || !bar?.bar_id) return;

    try {
      setIsLeaving(true);
      const data = await del(`/artists/bar/${bar.bar_id}/leave`, {
        artist_id: selectedArtistId,
      });

      if (data.success) {
        await refreshGroupsBars();
        if (refreshShows) await refreshShows();
        window.dispatchEvent(new CustomEvent("showsUpdated"));
        setShowLeaveConfirm(false);
        setSelectedArtistId(null);
        setFutureShows([]);
        setAlertModal({
          open: true,
          title: "Success",
          message: `Successfully left ${bar.name}. Artist members, bar owner, and bar admins have been notified.`,
        });
      } else {
        setAlertModal({
          open: true,
          title: "Error",
          message: data.error || "Failed to leave bar",
        });
      }
    } catch (error) {
      console.error("Error leaving bar:", error);
      setAlertModal({
        open: true,
        title: "Error",
        message: "Failed to leave bar. Please try again.",
      });
    } finally {
      setIsLeaving(false);
    }
  };

  // Set map center and marker position from bar coordinates
  useEffect(() => {
    if (isOpen && bar) {
      const lat = bar.latitude != null ? parseFloat(bar.latitude) : null;
      const lng = bar.longitude != null ? parseFloat(bar.longitude) : null;

      if (
        lat != null &&
        lng != null &&
        !isNaN(lat) &&
        !isNaN(lng) &&
        lat !== 0 &&
        lng !== 0
      ) {
        const center = { lat, lng };
        setMapCenter(center);
        setMarkerPosition(center);
        setMapZoom(14); // Zoom in on bar location
      } else {
        setMapCenter(defaultCenter);
        setMarkerPosition(null);
        setMapZoom(13);
      }
    }
  }, [isOpen, bar]);

  // Fetch user's artists linked to this bar
  useEffect(() => {
    if (isOpen && bar?.bar_id) {
      const fetchMyArtists = async () => {
        try {
          setLoadingArtists(true);
          const data = await get(`/artists/bar/${bar.bar_id}/my-artists`);
          if (data.success) {
            setMyArtists(data.artists || []);
          } else {
            setMyArtists([]);
          }
        } catch (error) {
          console.error("Error fetching my artists for bar:", error);
          setMyArtists([]);
        } finally {
          setLoadingArtists(false);
        }
      };
      fetchMyArtists();
    } else {
      setMyArtists([]);
    }
  }, [isOpen, bar?.bar_id]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMapExpanded(false);
      setMapCenter(defaultCenter);
      setMarkerPosition(null);
      setMapZoom(13);
      setShowMessageDropdown(false);
      setShowLeaveConfirm(false);
      setSelectedArtistId(null);
      setShowArtistSelector(false);
      setIsLeaving(false);
      setFutureShows([]);
      setLoadingFutureShows(false);
      setShowMemberBlockedModal(false);
      setMemberBlockedOwnerName("");
      setAlertModal({ open: false, title: "", message: "" });
    }
  }, [isOpen]);

  return (
    <div className="bar-modal-overlay" onClick={handleOverlayClick}>
      <div className="bar-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bar-modal-header">
          <div className="bar-modal-header-content">
            <h2>{bar.name || "Bar"}</h2>
            {bar.address && (
              <p className="bar-modal-address">📍 {bar.address}</p>
            )}
          </div>
          <div className="artist-profile-modal-header-actions">
            <button
              ref={messageButtonRef}
              type="button"
              className="artist-profile-message-btn"
              onClick={() => setShowMessageDropdown(true)}
              aria-label="Message bar contacts"
              disabled={!bar?.bar_id}
            >
              💬
            </button>
            {myArtists.length > 0 && (
              <button
                type="button"
                className="bar-modal-leave-btn"
                onClick={handleLeaveBarClick}
                aria-label="Leave bar"
                disabled={loadingArtists || isLeaving || loadingFutureShows}
              >
                Leave Bar
              </button>
            )}
            <button
              className="bar-modal-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
        </div>

        {showMessageDropdown && (
          <BarContactsDropdown
            isOpen={showMessageDropdown}
            onClose={() => setShowMessageDropdown(false)}
            barId={bar?.bar_id}
            buttonRef={messageButtonRef}
          />
        )}

        {showArtistSelector && (
          <div className="bar-modal-artist-selector-overlay">
            <div className="bar-modal-artist-selector">
              <h3>Choose Artist to Remove</h3>
              <p>Which artist would you like to remove from {bar.name}?</p>
              <div className="bar-modal-artist-list">
                {myArtists.map((artist) => (
                  <button
                    key={artist.artist_id}
                    type="button"
                    className="bar-modal-artist-option"
                    onClick={() => handleArtistSelect(artist.artist_id)}
                  >
                    {artist.stage_name}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="bar-modal-artist-selector-cancel"
                onClick={() => {
                  setShowArtistSelector(false);
                  setSelectedArtistId(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {showLeaveConfirm && (
          <div
            className="confirm-modal-overlay"
            role="presentation"
            onClick={() => {
              if (!isLeaving) {
                setShowLeaveConfirm(false);
                setSelectedArtistId(null);
                setFutureShows([]);
              }
            }}
          >
            <div
              className="confirm-modal leave-bar-confirm-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="leave-bar-confirm-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="confirm-modal-header">
                <h3 id="leave-bar-confirm-title">Leave Bar?</h3>
              </div>
              <div className="confirm-modal-body">
                {futureShows.length === 0 ? (
                  <>
                    <p>
                      This will remove {getSelectedArtistName()} from {bar.name}
                      . There are no future shows to delete. Past shows are not
                      affected.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      This will remove {getSelectedArtistName()} from {bar.name}{" "}
                      and delete the following future shows. Past shows are not
                      affected.
                    </p>
                    <div className="leave-bar-shows-list">
                      {futureShows.map((show) => (
                        <div key={show.show_id} className="leave-bar-show-item">
                          <strong>{formatShowDate(show.date)}</strong>
                          <span className="leave-bar-show-time">
                            {formatShowTime(show.start_time)} –{" "}
                            {formatShowTime(show.end_time)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="confirm-modal-footer">
                <button
                  type="button"
                  className="confirm-modal-secondary-btn"
                  onClick={() => {
                    setShowLeaveConfirm(false);
                    setSelectedArtistId(null);
                    setFutureShows([]);
                  }}
                  disabled={isLeaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="confirm-modal-danger-btn"
                  onClick={handleLeaveConfirm}
                  disabled={isLeaving}
                >
                  {isLeaving ? "Leave…" : "Leave"}
                </button>
              </div>
            </div>
          </div>
        )}

        <ConfirmModal
          open={showMemberBlockedModal}
          title="Leave Bar?"
          message={`You are not the owner of this group. Only ${memberBlockedOwnerName} has permission to remove this group from the bar.`}
          confirmLabel="OK"
          alertOnly
          onCancel={() => {
            setShowMemberBlockedModal(false);
            setMemberBlockedOwnerName("");
          }}
        />

        <ConfirmModal
          open={alertModal.open}
          title={alertModal.title}
          message={alertModal.message}
          confirmLabel="OK"
          alertOnly
          onCancel={() => {
            setAlertModal((prev) => ({ ...prev, open: false }));
            if (alertModal.title === "Success") onClose();
          }}
        />

        {/* Body */}
        <div className="bar-modal-body">
          {/* Hours Section */}
          {getWeeklyHoursDisplayLines(bar, formatTime).length > 0 && (
            <div className="onboarding-section">
              <h2 className="section-title">Hours</h2>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: "0.5rem 0 0",
                  lineHeight: 1.5,
                }}
              >
                {getWeeklyHoursDisplayLines(bar, formatTime).map((line, idx) => (
                  <li key={idx}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Location Section */}
          {bar.latitude != null && bar.longitude != null && (
            <div className="onboarding-section">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: mapExpanded ? "1rem" : "0",
                }}
              >
                <h2 className="section-title">Location</h2>
                <button
                  type="button"
                  onClick={() => setMapExpanded(!mapExpanded)}
                  className="bar-modal-map-toggle"
                  aria-label={mapExpanded ? "Collapse map" : "Expand map"}
                >
                  {mapExpanded ? "Hide Map" : "Show Map"}
                </button>
              </div>
              {mapExpanded && (
                <div
                  className="input-group full-width"
                  onClick={(e) => e.stopPropagation()}
                >
                  {isMapsLoaded ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={mapCenter}
                        zoom={mapZoom}
                        options={{ gestureHandling: "cooperative" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {markerPosition && (
                          <Marker position={markerPosition} draggable={false} />
                        )}
                      </GoogleMap>
                    </div>
                  ) : (
                    <div
                      style={{
                        ...mapContainerStyle,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f0f0f0",
                        color: "#666",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Loading map...
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Photos Section - moved to bottom */}
          {getOutdoorPhotos().length > 0 && (
            <div className="onboarding-section">
              <h2 className="section-title">Outdoor Photos</h2>
              <div className="bar-modal-photos">
                {getOutdoorPhotos().map((photo, index) => (
                  <img
                    key={index}
                    src={photo}
                    alt={`${bar.name} outdoor ${index + 1}`}
                    className="bar-modal-photo"
                    onError={(e) => {
                      e.currentTarget.src = defaultBarOutdoor;
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="onboarding-section">
            <h2 className="section-title">Indoor Photos</h2>
            <div className="bar-modal-photos">
              {getIndoorPhotos().map((photo, index) => (
                <img
                  key={index}
                  src={photo}
                  alt={`${bar.name} indoor ${index + 1}`}
                  className="bar-modal-photo"
                  onError={(e) => {
                    e.currentTarget.src = defaultBarIndoor;
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bar-modal-footer">
          <button className="bar-modal-close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarModal;
